"use client";

import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { UIMessage } from "ai";
import type { AgentChatApi } from "./agent-chat-types";
import {
  Plus,
  MoreHorizontal,
  X,
  ArrowUp,
  AtSign,
  Paperclip,
  Trash2,
  User,
  Mic,
} from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { ScrollArea } from "@/shadcn-components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
  useWebSpeechToText,
  type SpeechMode,
} from "@/components/desktop/agent/useWebSpeechToText";
import { VybeAgentMark } from "@/components/desktop/agent/VybeAgentMark";
import { FeatureGate } from "@/components/common";
import { AgentMarkdown } from "@/components/desktop/agent/AgentMarkdown";
import {
  AgentChartBubble,
  type AgentChartToolPart,
} from "@/components/desktop/agent/AgentChartBubble";
import { AgentChartDialog } from "@/components/desktop/agent/AgentChartDialog";
import type { AgentChartDialogPayload } from "@/lib/agent/agent-chart-payload";
import {
  DslToolBubble,
  type VybeDslToolPart,
} from "@/components/desktop/agent/DslToolBubble";
import {
  PlaceOrderToolBubble,
  type PlaceOrderToolPart,
} from "@/components/desktop/agent/PlaceOrderToolBubble";
import { AgentOrderConsentDialog } from "@/components/dialogs/AgentOrderConsentDialog";
import { clearAllDatasets } from "@/lib/agent/agent-dataset-cache";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shadcn-components/ui/dropdown-menu";
import { VybeSignInPanel } from "@/components/agent/VybeSignInPanel";
import { AgentCreditsMeter } from "@/components/agent/AgentCreditsMeter";
import { useAiCredits } from "@/lib/agent/ai-credits";
import { useVybeAuthStore } from "@/lib/vybe-auth";

export const AGENT_SUGGESTIONS = [
  "Summarize my open trades",
  "What is EURUSD doing today?",
  "Suggest a tight stop on XAUUSD",
  "Explain market depth",
] as const;

interface SuggestionChipProps {
  label: string;
  onPick: () => void;
  className?: string;
}

function SuggestionChip({ label, onPick, className }: SuggestionChipProps) {
  return (
    <button
      type="button"
      onClick={onPick}
      className={cn(
        "shrink-0 rounded-md border border-border bg-muted/40 px-2.5 py-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
        className,
      )}
    >
      {label}
    </button>
  );
}

interface BubbleProps {
  role: "agent" | "user";
  variant: "sidebar" | "mobile";
  children: React.ReactNode;
}

function Bubble({ role, variant, children }: BubbleProps) {
  const isAgent = role === "agent";
  const isMobile = variant === "mobile";
  return (
    <div className="flex min-w-0 gap-2.5">
      <div className="mt-0.5 flex shrink-0 items-start">
        {isAgent ? (
          <VybeAgentMark size={isMobile ? 16 : 14} tone="primary" />
        ) : (
          <User
            className={cn(
              "text-muted-foreground",
              isMobile ? "h-4 w-4" : "h-3.5 w-3.5",
            )}
          />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div
          className={cn(
            "mb-1 font-medium text-muted-foreground",
            isMobile ? "text-xs" : "text-[11px]",
          )}
        >
          {isAgent ? "Vybe Agent" : "You"}
        </div>
        <div
          className={cn(
            "min-w-0 break-words leading-relaxed text-foreground",
            isMobile ? "text-sm" : "text-xs",
            role === "user" && "whitespace-pre-wrap",
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}

export interface AgentChatViewProps {
  variant: "sidebar" | "mobile";
  speechMode: SpeechMode;
  chat: Pick<
    AgentChatApi,
    | "messages"
    | "sendMessage"
    | "status"
    | "error"
    | "setMessages"
    | "stop"
    | "pendingOrderConsent"
    | "approveOrderConsent"
    | "rejectOrderConsent"
  >;
  headerActions?: React.ReactNode;
  onClose?: () => void;
}

export function AgentChatView({
  variant,
  speechMode,
  chat,
  headerActions,
  onClose,
}: AgentChatViewProps) {
  const isMobile = variant === "mobile";
  const {
    messages,
    sendMessage,
    status,
    error,
    setMessages,
    stop,
    pendingOrderConsent,
    approveOrderConsent,
    rejectOrderConsent,
  } = chat;
  const signedIn = useVybeAuthStore((s) => !!s.token);
  const { credits, refresh: refreshCredits } = useAiCredits();

  const [input, setInput] = useState("");
  const [chartDialog, setChartDialog] =
    useState<AgentChartDialogPayload | null>(null);

  const handleOpenChartDialog = useCallback(
    (payload: AgentChartDialogPayload) => {
      setChartDialog(payload);
    },
    [],
  );

  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const busy = status === "streaming" || status === "submitted";
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const stopRef = useRef(stop);
  stopRef.current = stop;
  const sendRef = useRef(sendMessage);
  sendRef.current = sendMessage;
  const setMessagesRef = useRef(setMessages);
  setMessagesRef.current = setMessages;

  const sendText = useCallback((text: string) => {
    const trimmed = text.trim();
    if (!trimmed) return;
    void (async () => {
      if (busyRef.current) {
        try {
          await stopRef.current();
        } catch {
          // swallow stop errors so a new send still proceeds
        }
      }
      await sendRef.current({ text: trimmed });
    })();
  }, []);

  const onFinalTranscript = useCallback((text: string) => {
    setInput((prev) => (prev ? `${prev.trimEnd()} ${text}` : text));
  }, []);

  const onSendTranscript = useCallback(
    (text: string) => {
      sendText(text);
    },
    [sendText],
  );

  const {
    isSupported: isSpeechSupported,
    isListening,
    interimTranscript,
    toggle: toggleDictation,
  } = useWebSpeechToText({
    lang: "en-US",
    mode: speechMode,
    onFinalTranscript:
      speechMode === "append" ? onFinalTranscript : undefined,
    onSendTranscript:
      speechMode === "sendOnStop" ? onSendTranscript : undefined,
  });

  useEffect(() => {
    scrollAnchorRef.current?.scrollIntoView({ behavior: "auto", block: "end" });
  }, [messages.length, status]);

  // Re-read the authoritative server budget whenever a request settles (success or 429 exhaustion).
  useEffect(() => {
    if (status === "ready" || status === "error") {
      void refreshCredits();
    }
  }, [status, refreshCredits]);

  const handleSend = useCallback(() => {
    const text = input.trim();
    if (!text) return;
    setInput("");
    sendText(text);
  }, [input, sendText]);

  const handleNewChat = useCallback(() => {
    void (async () => {
      if (busyRef.current) {
        try {
          await stopRef.current();
        } catch {
          // ignore
        }
      }
      setMessagesRef.current([]);
      clearAllDatasets();
    })();
  }, []);

  const handleStop = useCallback(() => {
    void stopRef.current();
  }, []);

  const handlePickSuggestion = useCallback((label: string) => {
    if (speechMode === "sendOnStop") {
      sendText(label);
      return;
    }
    setInput(label);
    textareaRef.current?.focus();
  }, [speechMode, sendText]);

  const deferredMessages = useDeferredValue(messages);

  const messageList = useMemo(
    () => (
      <>
        {deferredMessages.map((m: UIMessage) => (
          <React.Fragment key={m.id}>
            {m.parts.map((part: UIMessage["parts"][number], idx: number) => {
              if (part.type === "text") {
                const t = part.text?.trim();
                if (!t) return null;
                const isAgent = m.role !== "user";
                return (
                  <Bubble
                    key={`${m.id}-txt-${idx}`}
                    role={isAgent ? "agent" : "user"}
                    variant={variant}
                  >
                    {isAgent ? (
                      <AgentMarkdown text={part.text} />
                    ) : (
                      part.text
                    )}
                  </Bubble>
                );
              }
              if (part.type === "tool-vybe_dsl") {
                const tp = part as VybeDslToolPart;
                return (
                  <DslToolBubble
                    key={`${m.id}-dsl-${tp.toolCallId}-${idx}`}
                    part={tp}
                  />
                );
              }
              if (part.type === "tool-place_order") {
                const op = part as PlaceOrderToolPart;
                return (
                  <PlaceOrderToolBubble
                    key={`${m.id}-order-${op.toolCallId}-${idx}`}
                    part={op}
                  />
                );
              }
              if (part.type === "tool-agent_chart") {
                const cp = part as AgentChartToolPart;
                return (
                  <AgentChartBubble
                    key={`${m.id}-chart-${cp.toolCallId}-${idx}`}
                    part={cp}
                    onOpenInDialog={handleOpenChartDialog}
                  />
                );
              }
              return null;
            })}
          </React.Fragment>
        ))}
      </>
    ),
    [deferredMessages, handleOpenChartDialog, variant],
  );

  const iconBtn = isMobile ? "h-11 w-11 p-0" : "h-6 w-6 p-0";
  const iconSize = isMobile ? "h-4 w-4" : "h-3.5 w-3.5";
  const headerH = isMobile ? "h-12" : "h-9";

  const micTitle = !isSpeechSupported
    ? "Speech recognition is not supported in this browser"
    : isListening
      ? speechMode === "sendOnStop"
        ? "Stop and send to agent"
        : "Stop dictation"
      : speechMode === "sendOnStop"
        ? "Tap to speak (sends when you stop)"
        : "Start dictation (English)";

  const introText = isMobile
    ? "Ask about positions, charts, watchlists, or the economic calendar. I run Vybe DSL in your browser when needed."
    : "Ask about positions, charts, watchlists, or the economic calendar. I run Vybe DSL actions on your desktop when needed.";

  // Gate the agent behind a Vybe Trader login (trading is unaffected).
  if (!signedIn) return <VybeSignInPanel variant={variant} />;

  return (
    <>
      <div className="flex h-full min-w-0 w-full flex-col overflow-hidden bg-background">
        <div
          className={cn(
            "flex shrink-0 items-center justify-between border-b border-border px-3",
            headerH,
          )}
        >
          <div className="flex min-w-0 items-center gap-1.5">
            <VybeAgentMark size={isMobile ? 16 : 14} tone="primary" />
            <span
              className={cn(
                "truncate font-medium",
                isMobile ? "text-sm" : "text-xs",
              )}
            >
              Vybe Agent
            </span>
            <span className="ml-1 rounded border border-border px-1 py-px text-[10px] uppercase tracking-wide text-muted-foreground">
              Beta
            </span>
          </div>
          <div className="flex items-center gap-0.5">
            <Button
              variant="ghost"
              size="sm"
              className={iconBtn}
              title={busy ? "Stop & start new chat" : "New chat"}
              type="button"
              onClick={handleNewChat}
            >
              <Plus className={iconSize} />
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={iconBtn}
                  title="More"
                  type="button"
                >
                  <MoreHorizontal className={iconSize} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[10rem]">
                <DropdownMenuItem onSelect={handleNewChat}>
                  <Plus className="h-3.5 w-3.5" />
                  New chat
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive focus:text-destructive"
                  onSelect={handleNewChat}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                  Clear conversation
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {headerActions}
            {onClose ? (
              <Button
                variant="ghost"
                size="sm"
                className={iconBtn}
                title="Close"
                type="button"
                onClick={onClose}
              >
                <X className={iconSize} />
              </Button>
            ) : null}
          </div>
        </div>

        <ScrollArea className="min-w-0 flex-1 [&_[data-radix-scroll-area-viewport]>div]:!block [&_[data-radix-scroll-area-viewport]>div]:min-w-0 [&_[data-radix-scroll-area-viewport]>div]:w-full">
          <div
            className={cn(
              "min-w-0 max-w-full space-y-5 py-4",
              isMobile ? "px-4" : "px-3",
            )}
          >
            <div className="space-y-2">
              <div className="flex items-center gap-1.5">
                <VybeAgentMark size={isMobile ? 16 : 14} tone="primary" />
                <span
                  className={cn("font-medium", isMobile ? "text-sm" : "text-xs")}
                >
                  How can I help with your trading?
                </span>
              </div>
              <p
                className={cn(
                  "leading-relaxed text-muted-foreground",
                  isMobile ? "text-xs" : "text-[11px]",
                )}
              >
                {introText}
              </p>
              {isMobile ? (
                <ScrollArea className="w-full whitespace-nowrap">
                  <div className="flex gap-1.5 pb-1 pt-1">
                    {AGENT_SUGGESTIONS.map((label) => (
                      <SuggestionChip
                        key={label}
                        label={label}
                        onPick={() => handlePickSuggestion(label)}
                        className="text-xs"
                      />
                    ))}
                  </div>
                </ScrollArea>
              ) : (
                <div className="flex flex-wrap gap-1.5 pt-1">
                  {AGENT_SUGGESTIONS.map((label) => (
                    <SuggestionChip
                      key={label}
                      label={label}
                      onPick={() => handlePickSuggestion(label)}
                      className="text-xs"
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="min-w-0 space-y-4">
              {messageList}

              {busy ? (
                <div className="flex min-w-0 gap-2.5">
                  <div className="mt-0.5 shrink-0">
                    <VybeAgentMark
                      size={isMobile ? 16 : 14}
                      tone="primary"
                      thinking
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div
                      className={cn(
                        "mb-1 font-medium text-muted-foreground",
                        isMobile ? "text-xs" : "text-[11px]",
                      )}
                    >
                      Vybe Agent
                    </div>
                    <div
                      className={cn(
                        "italic text-muted-foreground",
                        isMobile ? "text-sm" : "text-xs",
                      )}
                    >
                      Thinking…
                    </div>
                  </div>
                </div>
              ) : null}

              {error ? (
                <div
                  className={cn(
                    "break-words rounded-md border border-destructive/40 bg-destructive/10 px-2 py-1.5 text-destructive",
                    isMobile ? "text-xs" : "text-[11px]",
                  )}
                >
                  {error.message}
                </div>
              ) : null}

              <div ref={scrollAnchorRef} />
            </div>
          </div>
        </ScrollArea>

        <div
          className={cn(
            "min-w-0 shrink-0 space-y-2 border-t border-border",
            isMobile ? "p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]" : "p-2.5",
          )}
        >
          {isListening ? (
            <div
              className={cn(
                "flex items-start gap-1.5 leading-snug text-primary",
                isMobile ? "text-xs" : "text-[10px]",
              )}
            >
              <span className="mt-[3px] inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-primary" />
              <span className="min-w-0 flex-1">
                {speechMode === "sendOnStop" ? "Listening" : "Listening"}
                {interimTranscript ? (
                  <>
                    :{" "}
                    <span className="italic">
                      &ldquo;{interimTranscript}&rdquo;
                    </span>
                  </>
                ) : (
                  "…"
                )}
                {speechMode === "sendOnStop" && !interimTranscript ? (
                  <span className="text-muted-foreground">
                    {" "}
                    — release mic to send
                  </span>
                ) : null}
              </span>
            </div>
          ) : null}
          <div className="rounded-md border border-border bg-muted/30 transition-colors focus-within:border-primary/50">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder={
                busy
                  ? "Agent is working… type to queue your next message"
                  : "Ask the Vybe agent…"
              }
              rows={isMobile ? 3 : 2}
              className={cn(
                "w-full resize-none bg-transparent leading-relaxed outline-none placeholder:text-muted-foreground disabled:opacity-60",
                isMobile
                  ? "px-3 py-2.5 text-base" // ≥16px prevents iOS focus-zoom
                  : "px-2.5 py-2 text-xs",
              )}
            />
            <div
              className={cn(
                "flex min-w-0 items-center justify-between gap-1",
                isMobile ? "px-2 pb-2" : "px-1.5 pb-1.5",
              )}
            >
              <div className="flex min-w-0 items-center gap-0.5 overflow-hidden">
                <FeatureGate flag="agent.composer.mention">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={iconBtn}
                    aria-label="Mention context"
                    type="button"
                    disabled
                  >
                    <AtSign className={iconSize} />
                  </Button>
                </FeatureGate>
                <FeatureGate flag="agent.composer.attach">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={iconBtn}
                    aria-label="Attach file"
                    type="button"
                    disabled
                  >
                    <Paperclip className={iconSize} />
                  </Button>
                </FeatureGate>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    iconBtn,
                    isListening && "animate-pulse text-primary",
                  )}
                  onClick={toggleDictation}
                  disabled={!isSpeechSupported}
                  aria-pressed={isListening}
                  type="button"
                  title={micTitle}
                >
                  <Mic className={iconSize} />
                </Button>
              </div>
              <div className="flex items-center gap-1">
                {busy ? (
                  <Button
                    variant="outline"
                    size="sm"
                    type="button"
                    className={cn(
                      isMobile ? "h-11 px-3 text-xs" : "h-6 px-2 text-[10px]",
                    )}
                    onClick={handleStop}
                  >
                    Stop
                  </Button>
                ) : null}
                <Button
                  size="sm"
                  className={iconBtn}
                  disabled={input.trim().length === 0}
                  title={busy ? "Interrupt & send" : "Send"}
                  type="button"
                  onClick={handleSend}
                >
                  <ArrowUp className={iconSize} />
                </Button>
              </div>
            </div>
          </div>
          <AgentCreditsMeter credits={credits} variant={variant} />
          <div
            className={cn(
              "text-center text-muted-foreground",
              isMobile ? "text-[11px]" : "text-[10px]",
            )}
          >
            DSL runs locally in your browser; orders require explicit intent.
          </div>
        </div>
      </div>
      <AgentChartDialog
        open={chartDialog != null}
        payload={chartDialog}
        onOpenChange={(open) => {
          if (!open) setChartDialog(null);
        }}
      />
      <AgentOrderConsentDialog
        open={pendingOrderConsent != null}
        order={pendingOrderConsent}
        onApprove={approveOrderConsent}
        onReject={rejectOrderConsent}
      />
    </>
  );
}
